import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, Toast } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { talentTeachersApi, talentSpecializationsApi, talentStudentsApi } from '../../services/apiService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks/useResponsive';
import { createShadow } from '../../utils/styles';

type RouteProps = RouteProp<MainStackParamList, 'EditTeacher'>;
type NavProp = NativeStackNavigationProp<MainStackParamList>;

const FOREGROUND = '#414042';
const BORDER = '#E2E8F0';
const SECTION_BG = '#F1F5F9';
const MUTED = '#64748B';
const BRAND_RED = '#A9211F';

/* ── Chevron Down Icon ─────────────────────────────────────── */
function ChevronDownIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6 9l6 6 6-6" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/* ── Calendar Icon ─────────────────────────────────────────── */
function CalendarIcon({ size = 20, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/* ── X Icon for chip remove ──────────────────────────────── */
function XIcon({ size = 12, color = FOREGROUND }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M18 6L6 18M6 6l12 12" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/* ── Form Field Component ────────────────────────────────── */
function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <View style={fieldStyles.container}>
      <Text style={fieldStyles.label}>
        {required && <Text style={{ color: '#D62728' }}>*</Text>}
        {` ${label}`}
      </Text>
      {children}
    </View>
  );
}

/* ── Main Screen ─────────────────────────────────────────── */
export default function EditTeacherScreen() {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavProp>();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 20;

  const teacherId = route.params?.id;
  const specializationIdParam = route.params?.specializationId;
  const isCreateMode = !teacherId;

  // Form state
  const [name, setName] = useState('');
  const [eid, setEid] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  const [school, setSchool] = useState('');
  const [specializationId, setSpecializationId] = useState<number | null>(specializationIdParam || null);
  const [specializationName, setSpecializationName] = useState('');

  // Dropdown states
  const [showGenderDropdown, setShowGenderDropdown] = useState(false);
  const [showSpecDropdown, setShowSpecDropdown] = useState(false);
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [specOptions, setSpecOptions] = useState<{ id: number; name: string }[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<{ value: string; label: string }[]>([]);

  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [isLoading, setIsLoading] = useState(true);

  // Load teacher data (edit mode only)
  useEffect(() => {
    if (isCreateMode) {
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        const resp = await talentTeachersApi.get(teacherId);
        if (resp) {
          setName(resp.name || '');
          const rawEid = (resp.eid || '').replace(/[^0-9]/g, '');
          let fmtEid = rawEid;
          if (rawEid.length > 3) fmtEid = rawEid.slice(0, 3) + '-' + rawEid.slice(3);
          if (rawEid.length > 7) fmtEid = rawEid.slice(0, 3) + '-' + rawEid.slice(3, 7) + '-' + rawEid.slice(7);
          if (rawEid.length > 14) fmtEid = rawEid.slice(0, 3) + '-' + rawEid.slice(3, 7) + '-' + rawEid.slice(7, 14) + '-' + rawEid.slice(14);
          setEid(fmtEid);
          setEmail(resp.email || '');
          setPhone(resp.phone || '');
          setBirthDate(resp.birth_date || '');
          setGender(resp.gender || '');
          setSchool(resp.school || '');
          setSpecializationId(resp.specialization || null);
          setSpecializationName(resp.specialization_name || '');
        }
      } catch (e) {
        console.log('[EditTeacher] load failed:', e);
      }
      setIsLoading(false);
    })();
  }, [teacherId, isCreateMode]);

  // Load specialization options + resolve name from param
  useEffect(() => {
    (async () => {
      try {
        const resp = await talentSpecializationsApi.list();
        const items = resp?.results || resp || [];
        const mapped = items.map((s: any) => ({ id: s.id, name: s.name }));
        setSpecOptions(mapped);
        // Resolve name from param if not yet set
        if (specializationIdParam && !specializationName) {
          const match = mapped.find((s: any) => s.id === Number(specializationIdParam));
          if (match) setSpecializationName(match.name);
        }
      } catch (e) {
        console.log('[EditTeacher] load specs failed:', e);
      }
      try {
        const schools = await talentStudentsApi.schools();
        setSchoolOptions(schools);
      } catch (e) {
        console.log('[EditTeacher] load schools failed:', e);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!name.trim()) {
      setToastType('error');
      setToastMsg(isRTL ? 'يرجى إدخال الاسم' : 'Please enter a name');
      return;
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setToastType('error');
      setToastMsg(isRTL ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email');
      return;
    }
    const eidDigits = eid.replace(/[^0-9]/g, '');
    if (eidDigits && eidDigits.length !== 15) {
      setToastType('error');
      setToastMsg(isRTL ? `رقم الهوية يجب أن يكون 15 رقم (الحالي: ${eidDigits.length})` : `Emirates ID must be 15 digits (current: ${eidDigits.length})`);
      return;
    }
    const rawPhone = phone.replace(/\s+/g, '');
    if (rawPhone && !/^(\+971|971|0)?[0-9]{8,9}$/.test(rawPhone)) {
      setToastType('error');
      setToastMsg(isRTL ? 'يرجى إدخال رقم هاتف صحيح (مثال: 05X XXX XXXX)' : 'Please enter a valid UAE phone number');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        name: name.trim(),
        eid: eid.replace(/[^0-9]/g, ''),
        email: email.trim(),
        phone: phone.trim(),
        school: school.trim(),
        birth_date: birthDate || null,
        gender: gender || '',
        specialization: specializationId,
      };
      if (isCreateMode) {
        await talentTeachersApi.create(payload);
        setToastType('success');
        setToastMsg(isRTL ? 'تم إضافة المنسق بنجاح' : 'Teacher added successfully');
      } else {
        await talentTeachersApi.update(teacherId, payload);
        setToastType('success');
        setToastMsg(isRTL ? 'تم تحديث البيانات بنجاح' : 'Data updated successfully');
      }
      setTimeout(() => navigation.navigate('Talents' as any), 800);
    } catch (e) {
      console.log('[EditTeacher] save failed:', e);
      setToastType('error');
      setToastMsg(isRTL
        ? (isCreateMode ? 'فشل في إضافة المنسق' : 'فشل في تحديث البيانات')
        : (isCreateMode ? 'Failed to add teacher' : 'Failed to update data'));
    }
    setSaving(false);
  };

  const genderOptions = [
    { value: 'male', label: isRTL ? 'ذكر' : 'Male' },
    { value: 'female', label: isRTL ? 'أنثى' : 'Female' },
  ];

  const genderLabel = genderOptions.find(g => g.value === gender)?.label || '';
  const schoolLabel = schoolOptions.find(s => s.value === school)?.label || '';

  if (isLoading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: MUTED }}>
            {isRTL ? 'جاري التحميل...' : 'Loading...'}
          </Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
        <View style={{ padding: containerPadding, gap: 20 }}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: isRTL ? 'الرئيسية' : 'Home' },
              ...(isCreateMode
                ? [{ label: isRTL ? 'مبادرة مواهب' : 'Talents' }]
                : [{ label: isRTL ? 'تفاصيل المنسق' : 'Teacher Details' }]),
              { label: isRTL
                ? (isCreateMode ? 'إضافة منسق' : 'تعديل البيانات')
                : (isCreateMode ? 'Add Teacher' : 'Edit Data') },
            ]}
          />

          {/* Main Card */}
          <View style={[cardStyles.container, createShadow(0, 1, 3, '#000', 0.1)]}>
            <Text style={cardStyles.title}>
              {isRTL
                ? (isCreateMode ? 'إضافة منسق جديد' : 'تعديل بيانات المنسق')
                : (isCreateMode ? 'Add New Teacher' : 'Edit Teacher Data')}
            </Text>

            {/* Section 1: المعلومات الشخصية */}
            <View style={sectionStyles.container}>
              <Text style={sectionStyles.title}>
                {isRTL ? 'المعلومات الشخصية' : 'Personal Information'}
              </Text>

              {/* Row 1: Name + Birth Date */}
              <View style={sectionStyles.row}>
                <FormField label={isRTL ? 'تاريخ الميلاد' : 'Date of Birth'} required>
                  <View style={inputStyles.container}>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={birthDate}
                        onChange={(e: any) => setBirthDate(e.target.value)}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          fontSize: 14,
                          fontFamily: FONTS.regular,
                          color: FOREGROUND,
                          textAlign: isRTL ? 'right' : 'left',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0',
                          cursor: 'pointer',
                          minHeight: 21,
                        } as any}
                      />
                    ) : (
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <CalendarIcon size={20} color={MUTED} />
                        <TextInput
                          style={[inputStyles.input, { flex: 1, marginLeft: 8 }]}
                          value={birthDate}
                          onChangeText={setBirthDate}
                          placeholder="YYYY-MM-DD"
                          placeholderTextColor="#94A3B8"
                          textAlign={isRTL ? 'right' : 'left'}
                        />
                      </View>
                    )}
                  </View>
                </FormField>
                <FormField label={isRTL ? 'الاسم' : 'Name'} required>
                  <View style={inputStyles.container}>
                    <TextInput
                      style={inputStyles.input}
                      value={name}
                      onChangeText={setName}
                      placeholder={isRTL ? 'أدخل الاسم' : 'Enter name'}
                      placeholderTextColor="#94A3B8"
                      textAlign={isRTL ? 'right' : 'left'}
                    />
                  </View>
                </FormField>
              </View>

              {/* Row 1b: EID */}
              <View style={sectionStyles.row}>
                <View style={{ flex: 1 }} />
                <FormField label={isRTL ? 'رقم الهوية (EID)' : 'Emirates ID (EID)'}>
                  {Platform.OS === 'web' ? (
                    <div dir="ltr" style={{
                      backgroundColor: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: 8,
                      paddingLeft: 12, paddingRight: 12, paddingTop: 7, paddingBottom: 7,
                      minHeight: 36, boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}>
                      <input
                        type="text"
                        dir="ltr"
                        ref={(el: any) => { if (el) el.style.setProperty('direction', 'ltr', 'important'); }}
                        value={eid}
                        onChange={(e: any) => {
                          const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 15);
                          let formatted = digits;
                          if (digits.length > 3) formatted = digits.slice(0, 3) + '-' + digits.slice(3);
                          if (digits.length > 7) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7);
                          if (digits.length > 14) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7, 14) + '-' + digits.slice(14);
                          setEid(formatted);
                        }}
                        placeholder="784-XXXX-XXXXXXX-X"
                        maxLength={18}
                        style={{
                          width: '100%', border: 'none', outline: 'none',
                          backgroundColor: 'transparent', fontSize: 14,
                          fontFamily: FONTS.regular, color: FOREGROUND,
                          textAlign: 'left', padding: '0', minHeight: 21,
                        } as any}
                      />
                    </div>
                  ) : (
                    <View style={inputStyles.container}>
                      <TextInput
                        style={[inputStyles.input, { writingDirection: 'ltr', textAlign: 'left' }]}
                        value={eid}
                        onChangeText={(text) => {
                          const digits = text.replace(/[^0-9]/g, '').slice(0, 15);
                          let formatted = digits;
                          if (digits.length > 3) formatted = digits.slice(0, 3) + '-' + digits.slice(3);
                          if (digits.length > 7) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7);
                          if (digits.length > 14) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7, 14) + '-' + digits.slice(14);
                          setEid(formatted);
                        }}
                        placeholder="784-XXXX-XXXXXXX-X"
                        placeholderTextColor="#94A3B8"
                        keyboardType="number-pad"
                        maxLength={18}
                      />
                    </View>
                  )}
                </FormField>
              </View>

              {/* Row 2: Email + Phone */}
              <View style={sectionStyles.row}>
                <FormField label={isRTL ? 'الهاتف' : 'Phone'} required>
                  <View style={[inputStyles.container, { flexDirection: 'row', alignItems: 'center' }]}>
                    <TextInput
                      style={[inputStyles.input, { flex: 1 }]}
                      value={phone}
                      onChangeText={(text) => setPhone(text.replace(/[^0-9]/g, ''))}
                      placeholder={isRTL ? '5X XXX XXXX' : '5X XXX XXXX'}
                      placeholderTextColor="#94A3B8"
                      textAlign={isRTL ? 'right' : 'left'}
                      keyboardType="phone-pad"
                    />
                    <View style={{ borderLeftWidth: 1, borderLeftColor: BORDER, paddingLeft: 8, paddingRight: 4 }}>
                      <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: FOREGROUND }}>971+</Text>
                    </View>
                  </View>
                </FormField>
                <FormField label={isRTL ? 'البريد الالكتروني' : 'Email'} required>
                  <View style={inputStyles.container}>
                    <TextInput
                      style={inputStyles.input}
                      value={email}
                      onChangeText={setEmail}
                      placeholder="example@domain.com"
                      placeholderTextColor="#94A3B8"
                      textAlign={isRTL ? 'right' : 'left'}
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                  </View>
                </FormField>
              </View>

              {/* Row 3: Gender (right side only) */}
              <View style={sectionStyles.row}>
                <View style={{ flex: 1 }} />
                <FormField label={isRTL ? 'الجنس' : 'Gender'} required>
                  <TouchableOpacity
                    style={[inputStyles.container, { flexDirection: 'row', alignItems: 'center' }]}
                    onPress={() => setShowGenderDropdown(!showGenderDropdown)}
                  >
                    <ChevronDownIcon size={20} color={MUTED} />
                    <Text style={[inputStyles.input, { flex: 1 }]}>
                      {genderLabel || (isRTL ? 'اختر الجنس' : 'Select gender')}
                    </Text>
                  </TouchableOpacity>
                  {showGenderDropdown && (
                    <View style={dropdownStyles.menu}>
                      {genderOptions.map((opt) => (
                        <TouchableOpacity
                          key={opt.value}
                          style={[
                            dropdownStyles.item,
                            gender === opt.value && { backgroundColor: '#F1F5F9' },
                          ]}
                          onPress={() => {
                            setGender(opt.value);
                            setShowGenderDropdown(false);
                          }}
                        >
                          <Text style={dropdownStyles.itemText}>{opt.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </FormField>
              </View>
            </View>

            {/* Section 2: الملف التعريفي */}
            <View style={sectionStyles.container}>
              <Text style={sectionStyles.title}>
                {isRTL ? 'الملف التعريفي' : 'Profile'}
              </Text>

              {/* Row 1: School + Specialization */}
              <View style={sectionStyles.row}>
                <FormField label={isRTL ? 'المدرسة' : 'School'} required>
                  <TouchableOpacity
                    style={[inputStyles.container, { flexDirection: 'row', alignItems: 'center' }]}
                    onPress={() => setShowSchoolDropdown(!showSchoolDropdown)}
                  >
                    <ChevronDownIcon size={20} color={MUTED} />
                    <Text style={[inputStyles.input, { flex: 1 }]}>
                      {schoolLabel || (isRTL ? 'اختر المدرسة' : 'Select school')}
                    </Text>
                  </TouchableOpacity>
                  {showSchoolDropdown && (
                    <View style={[dropdownStyles.menu, { maxHeight: 250 }]}>
                      <ScrollView nestedScrollEnabled>
                        {schoolOptions.map((opt) => (
                          <TouchableOpacity
                            key={opt.value}
                            style={[
                              dropdownStyles.item,
                              school === opt.value && { backgroundColor: '#F1F5F9' },
                            ]}
                            onPress={() => {
                              setSchool(opt.value);
                              setShowSchoolDropdown(false);
                            }}
                          >
                            <Text style={dropdownStyles.itemText}>{opt.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </FormField>
              </View>

              {/* Row 2: Specializations (right side only) */}
              <View style={sectionStyles.row}>
                <View style={{ flex: 1 }} />
                <FormField label={isRTL ? 'المواهب' : 'Specializations'} required>
                  <TouchableOpacity
                    style={[inputStyles.container, { flexDirection: 'row', alignItems: 'center' }, specializationIdParam ? { opacity: 0.7 } : undefined]}
                    onPress={() => !specializationIdParam && setShowSpecDropdown(!showSpecDropdown)}
                    activeOpacity={specializationIdParam ? 1 : 0.7}
                  >
                    <ChevronDownIcon size={20} color={MUTED} />
                    <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 4 }}>
                      {specializationName ? (
                        <View style={chipStyles.chip}>
                          {!specializationIdParam && (
                            <TouchableOpacity
                              onPress={() => {
                                setSpecializationId(null);
                                setSpecializationName('');
                              }}
                            >
                              <XIcon size={12} color={FOREGROUND} />
                            </TouchableOpacity>
                          )}
                          <Text style={chipStyles.chipText}>{specializationName}</Text>
                        </View>
                      ) : (
                        <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: '#94A3B8', textAlign: 'right' }}>
                          {isRTL ? 'اختر الموهبة' : 'Select specialization'}
                        </Text>
                      )}
                    </View>
                  </TouchableOpacity>
                  {showSpecDropdown && (
                    <View style={dropdownStyles.menu}>
                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                        {specOptions.map((opt) => (
                          <TouchableOpacity
                            key={opt.id}
                            style={[
                              dropdownStyles.item,
                              specializationId === opt.id && { backgroundColor: '#F1F5F9' },
                            ]}
                            onPress={() => {
                              setSpecializationId(opt.id);
                              setSpecializationName(opt.name);
                              setShowSpecDropdown(false);
                            }}
                          >
                            <Text style={dropdownStyles.itemText}>{opt.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </FormField>
              </View>
            </View>

            {/* Footer: Save + Cancel */}
            <View style={footerStyles.container}>
              <TouchableOpacity
                style={[footerStyles.saveBtn, saving && { opacity: 0.6 }]}
                onPress={handleSave}
                disabled={saving}
              >
                <Text style={footerStyles.saveBtnText}>
                  {saving
                    ? (isRTL ? 'جاري الحفظ...' : 'Saving...')
                    : (isRTL ? 'حفظ' : 'Save')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[footerStyles.cancelBtn, createShadow(0, 1, 3, '#000', 0.1)]}
                onPress={() => {
                  if (navigation.canGoBack()) {
                    navigation.goBack();
                  } else {
                    navigation.navigate('Talents' as any);
                  }
                }}
              >
                <Text style={footerStyles.cancelBtnText}>
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
      <Toast visible={!!toastMsg} message={toastMsg} type={toastType} onHide={() => { setToastMsg(''); setToastType('info'); }} />
    </PageContainer>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                       */
/* ══════════════════════════════════════════════════════════════ */

const cardStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    padding: 20,
    gap: 20,
  },
  title: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const sectionStyles = StyleSheet.create({
  container: {
    backgroundColor: SECTION_BG,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 4,
    padding: 24,
    gap: 24,
  },
  title: {
    fontSize: 16,
    fontFamily: FONTS.bold || FONTS.medium,
    color: FOREGROUND,
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row',
    gap: 24,
  },
});

const fieldStyles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const inputStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  input: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
    padding: 0,
    margin: 0,
  },
});

const dropdownStyles = StyleSheet.create({
  menu: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    marginTop: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 10,
  },
  item: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  itemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const chipStyles = StyleSheet.create({
  chip: {
    backgroundColor: SECTION_BG,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 4,
    borderRadius: 2,
  },
  chipText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const footerStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    paddingTop: 16,
  },
  saveBtn: {
    backgroundColor: BRAND_RED,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveBtnText: {
    fontSize: 14,
    fontFamily: FONTS.bold || FONTS.medium,
    color: '#F8FAFC',
  },
  cancelBtn: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontFamily: FONTS.bold || FONTS.medium,
    color: FOREGROUND,
  },
});
